import React from 'react';
import {Formik, Form, Field} from 'formik';
import {Switch, TextField} from 'formik-material-ui';
import {makeStyles} from '@material-ui/styles';
import {useSnackbar} from 'notistack';
import Auth from '../../util/Auth';
import {Button, FormLabel, MenuItem, InputLabel, Typography} from '@material-ui/core';
import {v4} from 'uuid';
import {useMutation, useQuery, gql} from '@apollo/client';
import CreateableSelect from 'react-select/creatable';
import PERMISSIONS_LIST from '../../util/RolesEnum';
import {
    ADD_OTHER_LEASE,
    defaultOL,
    GET_ALL_OTHER_LEASES,
    GET_OPERATOR_DETAILS,
    ADD_OTHER_LEASE_OPERATOR
} from '../../components/otherleases/otherleaseQueries';
import Toolbar from "@material-ui/core/Toolbar";
import AppBar from "@material-ui/core/AppBar";

const hasPermissions = Auth.hasRole(PERMISSIONS_LIST.WRITE.WRITE_OTHER_LEASE);

const DetailPageStyles = makeStyles((theme) => ({
    paper: {
        padding: 25,
        marginBottom: '1rem',
        marginTop: '1rem',
    },
    TextField: {
        display: 'block',
        marginTop: 10,
        marginBottom: 10,
        paddingRight: '1em',
    },
    form: {
        marginTop: '20px',
    },
    title: {
        color: '#1976d2',
        fontSize: '1.75rem',
        textAlign: 'center',
        textDecoration: 'underline',
    },
    gdone: {
        display: 'grid',
        gridTemplateColumns: '25% 1fr',
        gap: '1rem',
    },
    left: {
        display: 'grid',
        gridTemplateRows: 'repeat(5, auto)',
    },
    right: {
        display: 'grid',
        gridTemplateRows: 'auto auto 1fr',
        gap: '1rem',
    },
    notes: {
        width: '100%',
        height: '100%',
    },
    buttons: {
        display: hasPermissions ? '' : 'none',
    },
    isNotAuthorized: {
        display: Auth.hasRole(PERMISSIONS_LIST.WRITE.WRITE_WELLS) ? 'none' : 'block',
    },
}));

// GraphQL query to get all operators
const GET_ALL_OPERATORS = gql`
    query GetAllOperators {
        Operator {
            id
            name
            address1
            city
            state
            zip
        }
    }
`;

const AddOtherLease = ({history}) => {
    const classes = DetailPageStyles();
    const {enqueueSnackbar} = useSnackbar();

    const [createOtherLease] = useMutation(ADD_OTHER_LEASE);

    const [addOperatorRelation] = useMutation(ADD_OTHER_LEASE_OPERATOR);

    // Fetch all operators for the Lessee dropdown
    const {data: operatorsData, loading: operatorsLoading, error: operatorsError} = useQuery(GET_ALL_OPERATORS);

    // Fetch operator details to auto fill
    const {
        data: operatorData,
        loading: operatorLoading,
        error: operatorError,
        refetch
    } = useQuery(GET_OPERATOR_DETAILS, {
        variables: {id: ''},
        skip: true, // Skip initial query
    });

    const initialOperators = operatorsData?.Operator || [];

    //Supply ID for new ROW (its a OtherLease)
    const newLeaseId = v4();

    const initialValues = {
        ...defaultOL,
        id: newLeaseId,
        lessee: '',
        operatorID: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        projectName: '', // Will be set to same as lessee
    };

    const handleLesseeChange = (option, setFieldValue) => {
        setFieldValue('lessee', option.name);
        setFieldValue('projectName', option.name); // Set project name to same as lessee
        setFieldValue('operatorID', option.id);

        // Fetch full operator details if needed
        if (option.id) {
            refetch({id: option.id}).then(({data}) => {
                const selectedOperator = data?.Operator?.[0] || initialOperators?.find((op) => op.id === option.id);
                if (selectedOperator) {
                    setFieldValue('address', selectedOperator.address1 || '');
                    setFieldValue('city', selectedOperator.city || '');
                    setFieldValue('state', selectedOperator.state || '');
                    setFieldValue('zip', selectedOperator.zip || '');
                } else {
                    setFieldValue('address', '');
                    setFieldValue('city', '');
                    setFieldValue('state', '');
                    setFieldValue('zip', '');
                }
            }).catch((err) => {
                console.error('Error fetching operator details:', err);
                setFieldValue('address', '');
                setFieldValue('city', '');
                setFieldValue('state', '');
                setFieldValue('zip', '');
            });
        } else {
            // Clear address fields when no operator selected
            setFieldValue('address', '');
            setFieldValue('city', '');
            setFieldValue('state', '');
            setFieldValue('zip', '');
        }
    };

    if (operatorsLoading) return <div>Loading operators...</div>;
    if (operatorsError) return <div>Error loading operators: {operatorsError.message}</div>;

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values, actions) => {
                return createOtherLease({
                    variables: {
                        id: values.id,
                        dateOfLease: values.dateOfLease,
                        lessee: values.lessee,
                        termOfLease: values.termOfLease,
                        dateOfExpiration: values.dateOfExpiration,
                        type: values.type,
                        original: values.original,
                        volAndPage: values.volAndPage,
                        datePaid: values.datePaid,
                        familyId: values.familyId,
                        projectName: values.projectName,
                        comments: values.comments,
                        status: values.status,
                        sizeOfPipeline: values.sizeOfPipeline,
                        lengthOfPipeline: values.lengthOfPipeline,
                        costPerUnit: values.costPerUnit,
                        acreage: values.acreage,
                        contactName: values.contactName,
                        contactAddress: values.contactAddress,
                        contactPhone: values.contactPhone,
                        contactEmail: values.contactEmail,
                    },
                    update: (store, {data: {CreateOtherLease}}) => {
                        try {
                            const AllOtherLeases = store.readQuery({query: GET_ALL_OTHER_LEASES});
                            const data = {OtherLease: [...AllOtherLeases.OtherLease, CreateOtherLease]};
                            store.writeQuery({query: GET_ALL_OTHER_LEASES, data: data});
                        } catch (e) {
                            console.log('Could not add lease to all leases query due to it not being run yet');
                        }
                    },
                })
                    .then(async (data) => {
                        // If an operator was selected, create the relationship
                        if (values.operatorID) {
                            try {
                                await addOperatorRelation({
                                    variables: {
                                        from: {id: values.operatorID},
                                        to: {id: values.id}
                                    }
                                });
                            } catch (err) {
                                console.error('Error adding operator relationship:', err);
                                enqueueSnackbar('Lease created but failed to link operator', {variant: 'warning'});
                            }
                        }

                        enqueueSnackbar('Lease successfully Added', {variant: 'success'});
                        history.push(`/OtherLeases/${values.id}`);
                        actions.setSubmitting(false);
                    })
                    .catch((err) => {
                        enqueueSnackbar(err.toString(), {variant: 'error'});
                        actions.setSubmitting(false);
                    });
            }}
        >
            {(props) => {
                return (
                    <Form>
                        <AppBar position='static'>
                            <Toolbar
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row-reverse',
                                    marginRight: '25px',
                                }}
                            >
                                <div>
                                    <Button className={classes.buttons} variant='contained' color='primary'
                                            type='submit'>
                                        Add Lease
                                    </Button>
                                </div>
                                <Typography variant='h5' className={classes.isNotAuthorized}>
                                    Not Authorized To Make Changes
                                </Typography>
                            </Toolbar>
                        </AppBar>
                        <div className={classes.gdone}>
                            <div className={classes.left}>
                                <div style={{width: 300}}>
                                    <InputLabel>Lessee</InputLabel>
                                    <Field name='lessee' id='lessee'>
                                        {({field, form}) => (
                                            // Dynamic select of all operators
                                            <CreateableSelect
                                                placeholder="...Select"
                                                options={initialOperators}
                                                getOptionLabel={(option) => option.name}
                                                getOptionValue={(option) => option.id}
                                                onChange={(option) => handleLesseeChange(option, form.setFieldValue)}
                                                styles={{
                                                    option: (provided, state) => ({...provided, fontFamily: 'Roboto'}),
                                                    singleValue: (provided, state) => ({
                                                        ...provided,
                                                        fontFamily: 'Roboto'
                                                    }),
                                                }}
                                            />
                                        )}
                                    </Field>
                                </div>
                                <Field
                                    name='address'
                                    label='Address'
                                    placeholder='Address'
                                    component={TextField}
                                    className={classes.TextField}
                                    disabled
                                />
                                <Field
                                    name='city'
                                    label='City'
                                    placeholder='City'
                                    component={TextField}
                                    className={classes.TextField}
                                    disabled
                                />
                                <Field
                                    name='state'
                                    label='State'
                                    placeholder='State'
                                    component={TextField}
                                    className={classes.TextField}
                                    disabled
                                />
                                <Field
                                    name='zip'
                                    label='Zip'
                                    placeholder='Zip'
                                    component={TextField}
                                    className={classes.TextField}
                                    disabled
                                />
                                <Field
                                    name='termOfLease'
                                    placeholder='Lease Term'
                                    component={TextField}
                                    label='Lease Term'
                                    className={classes.TextField}
                                />
                                <Field
                                    name='dateOfLease'
                                    placeholder='MM-DD-YYYY'
                                    type='date'
                                    component={TextField}
                                    label='Date of Lease'
                                    className={classes.TextField}
                                    InputLabelProps={{shrink: true}}
                                />
                                <Field
                                    name='dateOfExpiration'
                                    label='Lease Termination'
                                    placeholder='MM-DD-YYYY'
                                    type='date'
                                    component={TextField}
                                    className={classes.TextField}
                                    InputLabelProps={{shrink: true}}
                                />
                            </div>
                            <div className={classes.right}>
                                <div style={{display: 'flex'}}>
                                    <Field
                                        name='type'
                                        margin='normal'
                                        select
                                        label='Type'
                                        component={TextField}
                                        className={classes.TextField}
                                        InputLabelProps={{shrink: true}}
                                    >
                                        <MenuItem value='GRAZING'>GRAZING</MenuItem>
                                        <MenuItem value='FARMING'>FARMING</MenuItem>
                                    </Field>
                                    <Field
                                        name='status'
                                        margin='normal'
                                        select
                                        label='Status'
                                        component={TextField}
                                        className={classes.TextField}
                                        InputLabelProps={{shrink: true}}
                                    >
                                        <MenuItem value='Active'>Active</MenuItem>
                                        <MenuItem value='Inactive'>Inactive</MenuItem>
                                    </Field>
                                </div>
                                <Field
                                    name='comments'
                                    placeholder='Notes'
                                    label='Notes'
                                    component={TextField}
                                    multiline={true}
                                    rows={4}
                                    variant='outlined'
                                    className={classes.notes}
                                    fullWidth
                                />
                            </div>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default AddOtherLease;